// The ⌘K palette's Recent group: the signed-in person's recently viewed
// records, GET /api/recent-records (apps/server/src/recent.mjs). Record pages
// POST to it when they open. Parsing is here, free of React, so it is unit
// tested (recentRecords.test.ts).
import { useEffect, useState } from 'react';

import { isObjectKey } from '../objects/routes';
import type { ObjectKey } from '../navigation';

export const RECENT_URL = '/api/recent-records';

export interface RecentRecord {
  object: ObjectKey;
  id: string;
  label: string;
}

type Fetch = (input: string, init?: RequestInit) => Promise<Response>;

/** The records, dropping any row that does not name a known object and an id. */
export function parseRecentRecords(payload: unknown): RecentRecord[] {
  const rows = (payload as { records?: unknown } | null)?.records;
  if (!Array.isArray(rows)) return [];
  return rows.flatMap((row: Record<string, unknown>) =>
    typeof row?.object === 'string' && isObjectKey(row.object) && typeof row.id === 'string' && row.id !== ''
      ? [{ object: row.object, id: row.id, label: typeof row.label === 'string' && row.label.trim() ? row.label : 'Untitled' }]
      : [],
  );
}

/** Says a record page was opened. A failure is ignored: the page works without it. */
export async function recordView(fetcher: Fetch, object: ObjectKey, id: string): Promise<void> {
  try {
    await fetcher(RECENT_URL, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ object, id }),
    });
  } catch {
    // Nothing to show.
  }
}

/** Loads the recent records each time the palette opens. A failed load, or a 403, shows none. */
export function useRecentRecords(fetcher: Fetch, open: boolean): RecentRecord[] {
  const [records, setRecords] = useState<RecentRecord[]>([]);
  useEffect(() => {
    if (!open) return undefined;
    const controller = new AbortController();
    (async () => {
      try {
        const response = await fetcher(RECENT_URL, { signal: controller.signal });
        const found = response.ok ? parseRecentRecords(await response.json()) : [];
        if (!controller.signal.aborted) setRecords(found);
      } catch {
        if (!controller.signal.aborted) setRecords([]);
      }
    })();
    return () => controller.abort();
  }, [fetcher, open]);
  return records;
}
